import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { animate, motion, useInView, useMotionValue, useMotionValueEvent, useReducedMotion } from 'framer-motion';
import {
    BarChart3, Bell, CalendarDays, ChevronDown, Crown, LayoutDashboard, MessageCircle, PackageOpen,
    Receipt, Search, Send, Settings, TrendingUp, UsersRound, Wallet, CalendarCheck2,
} from 'lucide-react';
import styles from './DashboardShot.module.css';
import logo from '../../assets/hirsta-logo-navy.png';

// =============================================================================
// A crisp, animated "screenshot" of the Hirsta dashboard, drawn in code.
// Designed at a fixed width and scaled to fit, so it always looks like a real product shot.
// Sample data only.
// =============================================================================
const DESIGN_W = 1180;

const TREND = [18, 22, 19, 25, 31, 27, 24, 29, 35, 33, 28, 30, 38, 42, 36, 34, 40, 45, 41, 39, 47, 52, 46, 44, 50, 55, 49, 53, 58, 62];
const KPIS = [
    { icon: Receipt, label: 'Total sales', value: 48250, money: true, delta: '+12.4%' },
    { icon: TrendingUp, label: 'Fresh collection', value: 39800, money: true, delta: '+9.1%' },
    { icon: Wallet, label: 'Advance used', value: 8450, money: true, delta: 'Prepaid + khata' },
    { icon: PackageOpen, label: 'Packages sold', value: 14000, money: true, delta: '4 sold' },
    { icon: CalendarDays, label: 'Appointments', value: 26, money: false, delta: '6 upcoming' },
];
const COLLECTION = [
    { label: 'UPI', pct: 46, color: '#673de6' },
    { label: 'Cash', pct: 28, color: '#009e5b' },
    { label: 'Card', pct: 14, color: '#331c74' },
    { label: 'Prepaid', pct: 12, color: '#fc5185' },
];
const STAFF = [['Riya', 18600], ['Aman', 14200], ['Neha', 11900], ['Kabir', 8700]];
const NEXT = [['4:30 PM', 'Priya S.', 'Facial', 'in 12 min'], ['5:00 PM', 'Rahul V.', 'Haircut', 'in 42 min'], ['5:30 PM', 'Anjali K.', 'Hair Spa', 'Booked']];
const NAV = [
    [LayoutDashboard, 'Dashboard', true], [Receipt, 'Create bill'], [CalendarDays, 'Appointments'], [Crown, 'Memberships'],
    [PackageOpen, 'Packages'], [UsersRound, 'Clients'], [Wallet, 'Staff'], [BarChart3, 'Analytics'], [MessageCircle, 'Marketing'],
];

const inr = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;

function useCount(target, run) {
    const mv = useMotionValue(0);
    const [v, setV] = useState(0);
    useMotionValueEvent(mv, 'change', setV);
    useEffect(() => {
        if (!run) return undefined;
        const c = animate(mv, target, { duration: 1.4, ease: [0.22, 1, 0.36, 1] });
        return () => c.stop();
    }, [run, target, mv]);
    return v;
}

function Kpi({ k, run }) {
    const Icon = k.icon;
    const v = useCount(k.value, run);
    return (
        <div className={styles.kpi}>
            <div className={styles.kpiTop}>
                <span className={styles.kpiIcon}><Icon size={13} /></span>
                <span className={styles.kpiDelta}>{k.delta}</span>
            </div>
            <span className={styles.kpiLabel}>{k.label}</span>
            <strong className={styles.kpiValue}>{k.money ? inr(v) : Math.round(v)}</strong>
        </div>
    );
}

function TrendChart({ run }) {
    const w = 560; const h = 170; const max = 70;
    const pts = TREND.map((v, i) => [(i / (TREND.length - 1)) * w, h - (v / max) * h]);
    const line = pts.reduce((d, [x, y], i) => {
        if (i === 0) return `M${x},${y}`;
        const [px, py] = pts[i - 1];
        const cx = (px + x) / 2;
        return `${d} C${cx},${py} ${cx},${y} ${x},${y}`;
    }, '');
    const area = `${line} L${w},${h} L0,${h} Z`;
    const last = pts[pts.length - 1];
    return (
        <svg viewBox={`0 0 ${w} ${h + 4}`} className={styles.chart} preserveAspectRatio="none">
            <defs>
                <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#7b66ff" stopOpacity="0.32" />
                    <stop offset="1" stopColor="#7b66ff" stopOpacity="0" />
                </linearGradient>
            </defs>
            {[0.25, 0.5, 0.75].map((g) => <line key={g} x1="0" x2={w} y1={h * g} y2={h * g} className={styles.grid} />)}
            <motion.path d={area} fill="url(#trendFill)" initial={{ opacity: 0 }} animate={{ opacity: run ? 1 : 0 }} transition={{ duration: 0.8, delay: 0.6 }} />
            <motion.path d={line} fill="none" stroke="#673de6" strokeWidth="2.5" strokeLinecap="round"
                initial={{ pathLength: 0 }} animate={{ pathLength: run ? 1 : 0 }} transition={{ duration: 1.6, ease: [0.65, 0, 0.35, 1] }} />
            <motion.g initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: run ? 1 : 0, scale: run ? 1 : 0.5 }} transition={{ delay: 1.5 }} style={{ transformOrigin: `${last[0]}px ${last[1]}px` }}>
                <circle cx={last[0]} cy={last[1]} r="9" fill="#673de6" opacity="0.18" />
                <circle cx={last[0]} cy={last[1]} r="4.5" fill="#fff" stroke="#673de6" strokeWidth="2.5" />
            </motion.g>
        </svg>
    );
}

function Donut({ run }) {
    const r = 46; const c = 2 * Math.PI * r;
    let offset = 0;
    return (
        <div className={styles.donutWrap}>
            <svg viewBox="0 0 120 120" className={styles.donut}>
                <circle cx="60" cy="60" r={r} fill="none" stroke="#f1f0f7" strokeWidth="14" />
                {COLLECTION.map((s, i) => {
                    const len = (s.pct / 100) * c;
                    const el = (
                        <motion.circle key={s.label} cx="60" cy="60" r={r} fill="none" stroke={s.color} strokeWidth="14"
                            strokeDasharray={`${len - 2} ${c}`} strokeDashoffset={-offset} transform="rotate(-90 60 60)"
                            initial={{ opacity: 0 }} animate={{ opacity: run ? 1 : 0 }} transition={{ delay: 0.5 + i * 0.15, duration: 0.5 }} />
                    );
                    offset += len;
                    return el;
                })}
            </svg>
            <div className={styles.donutCenter}><small>Collected</small><strong>₹39.8k</strong></div>
        </div>
    );
}

export default function DashboardShot() {
    const outer = useRef(null);
    const [scale, setScale] = useState(1);
    const reduce = useReducedMotion();
    const inView = useInView(outer, { once: true, amount: 0.25 });
    const run = reduce ? true : inView;

    useLayoutEffect(() => {
        const el = outer.current;
        if (!el) return undefined;
        const fit = () => setScale(el.clientWidth / DESIGN_W);
        fit();
        const ro = new ResizeObserver(fit);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    return (
        <div ref={outer} className={styles.outer} style={{ height: 712 * scale }} role="img" aria-label="Hirsta dashboard with sales, collection, staff and appointments (sample data)">
            <div className={styles.window} style={{ width: DESIGN_W, transform: `scale(${scale})` }}>
                {/* browser chrome */}
                <div className={styles.chrome}>
                    <span className={styles.dots}><i /><i /><i /></span>
                    <span className={styles.url}><span className={styles.lock} /> app.hirsta.com/dashboard</span>
                    <span className={styles.chromeEnd} />
                </div>

                <div className={styles.app}>
                    {/* sidebar */}
                    <aside className={styles.side}>
                        <img src={logo} alt="" className={styles.sideLogo} />
                        <nav>
                            {NAV.map(([Icon, label, active]) => (
                                <span key={label} className={`${styles.navItem} ${active ? styles.navActive : ''}`}><Icon size={14} /> {label}</span>
                            ))}
                        </nav>
                        <div className={styles.sidePromo}>
                            <strong>Today</strong>
                            <span>37 bills, 22 clients</span>
                            <i><b style={{ width: '72%' }} /></i>
                            <small>72% of daily target</small>
                        </div>
                    </aside>

                    {/* main */}
                    <main className={styles.main}>
                        <div className={styles.top}>
                            <div className={styles.crumb}>
                                <span className={styles.branch}>Glow Studio, Sector 18 <ChevronDown size={12} /></span>
                                <span className={styles.crumbSep}>/</span> Dashboard
                            </div>
                            <div className={styles.search}><Search size={12} /> Search clients, bills…</div>
                            <div className={styles.topRight}>
                                <span className={styles.eod}><Send size={12} /> Send EOD</span>
                                <span className={styles.iconBtn}><Bell size={13} /><i /></span>
                                <span className={styles.iconBtn}><Settings size={13} /></span>
                                <span className={styles.user}><b>RS</b><span><strong>Rohit Sharma</strong><small>Owner</small></span></span>
                            </div>
                        </div>

                        <div className={styles.headRow}>
                            <div><h3>Good evening, Rohit</h3><p>Here is how Glow Studio is doing today.</p></div>
                            <span className={styles.range}><CalendarDays size={12} /> Today <ChevronDown size={12} /></span>
                        </div>

                        <div className={styles.kpis}>{KPIS.map((k) => <Kpi key={k.label} k={k} run={run} />)}</div>

                        <div className={styles.row2}>
                            <section className={`${styles.panel} ${styles.trend}`}>
                                <header><strong>Revenue trend</strong><span>Last 30 days</span><b className={styles.up}>+18.2%</b></header>
                                <TrendChart run={run} />
                                <div className={styles.axis}>{['11 Sep', '18 Sep', '25 Sep', '02 Oct', '08 Oct'].map((d) => <span key={d}>{d}</span>)}</div>
                            </section>
                            <section className={`${styles.panel} ${styles.collect}`}>
                                <header><strong>Collection</strong><span>Today</span></header>
                                <div className={styles.collectBody}>
                                    <Donut run={run} />
                                    <ul className={styles.legend}>
                                        {COLLECTION.map((s) => <li key={s.label}><i style={{ background: s.color }} /> {s.label} <b>{s.pct}%</b></li>)}
                                    </ul>
                                </div>
                            </section>
                        </div>

                        <div className={styles.row3}>
                            <section className={styles.panel}>
                                <header><strong>Top performers</strong><span>Sales + package services</span></header>
                                {STAFF.map(([n, v], i) => (
                                    <div key={n} className={styles.staff}>
                                        <span className={styles.rank}>{i + 1}</span>
                                        <span className={styles.staffName}>{n}</span>
                                        <span className={styles.bar}><motion.b initial={{ width: 0 }} animate={{ width: run ? `${(v / STAFF[0][1]) * 100}%` : 0 }} transition={{ duration: 1, delay: 0.4 + i * 0.1, ease: [0.22, 1, 0.36, 1] }} /></span>
                                        <strong>{inr(v)}</strong>
                                    </div>
                                ))}
                            </section>
                            <section className={styles.panel}>
                                <header><strong>Live board</strong><span className={styles.live}><i /> Live</span></header>
                                {NEXT.map(([t, n, s, st], i) => (
                                    <div key={t} className={styles.appt}>
                                        <time>{t}</time>
                                        <span><strong>{n}</strong><small>{s}</small></span>
                                        <em className={i === 0 ? styles.soon : ''}>{st}</em>
                                    </div>
                                ))}
                            </section>
                            <section className={`${styles.panel} ${styles.pkgPanel}`}>
                                <header><strong>Packages</strong><span>Active</span></header>
                                <div className={styles.pkgBig}><CalendarCheck2 size={16} /> <strong>42</strong><small>clients with services left</small></div>
                                <div className={styles.pkgRow}><span>Used this week</span><b>31</b></div>
                                <div className={styles.pkgRow}><span>Expiring in 7 days</span><b className={styles.warn}>5</b></div>
                            </section>
                        </div>
                    </main>
                </div>
            </div>
        </div>
    );
}
